import React from 'react';
import { Card, CardContent, CardHeader } from '@/Components/ui/card';
import { Badge } from '@/Components/ui/badge';
import { Button } from '@/Components/ui/button';
import { Separator } from '@/Components/ui/separator';
import { Stack } from '@/Components/ui/layout';
import { Heading } from '@/Components/ui/typography';
import { Text } from '@/Components/ui/typography';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export const PricingCard = ({
  name,
  idealFor,
  price,
  credits,
  features,
  isPopular = false,
  billingCycle = 'monthly',
  isCurrent = false,
  requested = false,
  hasPending = false,
  actionLabel = 'Request upgrade',
  actionBusy = false,
  onRequest,
  onContactSales,
}) => {
  const formatPrice = (amount) => {
    // ₹ INR per month. 0 / unpriced = Enterprise custom pricing.
    if (!amount || amount === 0) return 'Custom';
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  const getBillingLabel = () => {
    switch (billingCycle) {
      case 'quarterly':
        return '/quarter';
      case 'annual':
        return '/year';
      default:
        return '/month';
    }
  };

  return (
    <Card
      className={cn(
        "relative flex flex-col h-full transition-all hover:shadow-lg",
        isPopular && "border-2 border-primary shadow-lg md:scale-105"
      )}
    >
      {isPopular && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold">
          Most Popular
        </Badge>
      )}
      
      <CardHeader className="pb-4">
        <Stack spacing="sm">
          <Heading level="h3" className="text-xl font-bold text-foreground">
            {name}
          </Heading>
          <Text size="sm" className="text-muted-foreground min-h-[40px]">
            {idealFor}
          </Text>
          <div className="flex items-baseline gap-1 mt-2">
            <span className="text-4xl font-bold text-primary">
              {formatPrice(price)}
            </span>
            {price > 0 && name !== 'Enterprise' && (
              <span className="text-muted-foreground text-sm">
                {getBillingLabel()}
              </span>
            )}
            {name === 'Enterprise' && (
              <span className="text-muted-foreground text-sm">
                Pricing
              </span>
            )}
          </div>
          {credits > 0 && (
            <Text size="sm" className="text-muted-foreground mt-1">
              <strong className="text-foreground font-bold">{credits} Credits included</strong>
            </Text>
          )}
        </Stack>
      </CardHeader>

      <Separator className="my-4" />

      <CardContent className="flex-1 flex flex-col">
        <Stack spacing="md" className="flex-1">
          <ul className="flex flex-col gap-3 list-none">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <Text size="sm" className="text-foreground">
                  {feature}
                </Text>
              </li>
            ))}
          </ul>
        </Stack>

        <div className="mt-6 space-y-2">
          {name === 'Enterprise' ? (
            <>
              <Button variant="outline" className="w-full" onClick={onContactSales}>
                Contact Sales
              </Button>
              <div className="h-10" aria-hidden="true"></div>
            </>
          ) : isCurrent ? (
            <Button variant="outline" className="w-full" disabled>
              Current plan
            </Button>
          ) : requested ? (
            <Button variant="outline" className="w-full" disabled>
              Requested — pending
            </Button>
          ) : (
            <>
              <Button
                className={cn(
                  "w-full",
                  isPopular
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                )}
                onClick={onRequest}
                disabled={hasPending || actionBusy}
                title={hasPending ? 'You already have a pending plan request' : undefined}
              >
                {actionLabel}
              </Button>
              <Button variant="ghost" className="w-full" onClick={onContactSales}>
                Schedule a Demo
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
